/**
 * Auth API client — talks to mttr/app_api (login + /me).
 *
 * MTTR is self-contained: this posts the raw credential to OUR backend, which
 * verifies it locally against the imported directory (no smartbox3/SWS call)
 * and returns a signed token. The token is sent on subsequent requests via the
 * `access-token` header (matching the gen5-pwa convention the backend accepts).
 */

import { apiBase } from "./apiClient";

export type Role = "platform" | "customer" | "vendor";

export interface AuthUser {
  user_id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  company_id: number | null;
  company_name: string | null;
  role: Role;
  is_super_user: boolean;
  /** MTTR RBAC capability keys (e.g. "minmax.edit", "users.manage"). */
  capabilities?: string[];
  /** Company web-display timezone (IANA), e.g. "America/Chicago"; null = UTC. */
  display_timezone?: string | null;
}

export interface LoginResult {
  token: string;
  user: AuthUser;
}

export class AuthApiError extends Error {
  constructor(message: string, readonly status?: number) {
    super(message);
    this.name = "AuthApiError";
  }
}

function baseUrl(): string | null {
  // Login keeps its own token-less fetch + response shape (see below), so it
  // resolves the base itself. `apiBase()` throws when unset; the login flow
  // instead treats "unset" as a disabled feature, so map that back to null.
  try {
    return apiBase();
  } catch {
    return null;
  }
}

async function readError(resp: Response): Promise<string> {
  try {
    const j = await resp.json();
    if (j && typeof j.error === "string") return j.error;
  } catch {
    /* fall through */
  }
  return resp.statusText || `HTTP ${resp.status}`;
}

/** Verify credentials against the backend; returns token + user on success. */
export async function login(
  identifier: string,
  password: string,
): Promise<LoginResult> {
  const base = baseUrl();
  if (base === null) {
    throw new AuthApiError("appApiBaseUrl is unset; login unavailable");
  }
  const resp = await fetch(`${base}/api/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ identifier, password }),
    cache: "no-store",
  });
  if (!resp.ok) {
    throw new AuthApiError(await readError(resp), resp.status);
  }
  return (await resp.json()) as LoginResult;
}

/** Request a password-reset email. Always resolves (the backend returns 200
 *  whether or not the account exists -- no enumeration); throws only on a
 *  transport/5xx error or unset base. */
export async function requestPasswordReset(identifier: string): Promise<void> {
  const base = baseUrl();
  if (base === null) {
    throw new AuthApiError("appApiBaseUrl is unset; reset unavailable");
  }
  const resp = await fetch(`${base}/api/password/forgot`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ identifier }),
    cache: "no-store",
  });
  if (!resp.ok) {
    throw new AuthApiError(await readError(resp), resp.status);
  }
}

/** Consume a reset token + set a new password. Throws AuthApiError (status 400)
 *  on an invalid/expired token or a too-short password. */
export async function submitPasswordReset(
  token: string,
  password: string,
): Promise<void> {
  const base = baseUrl();
  if (base === null) {
    throw new AuthApiError("appApiBaseUrl is unset; reset unavailable");
  }
  const resp = await fetch(`${base}/api/password/reset`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ token, password }),
    cache: "no-store",
  });
  if (!resp.ok) {
    throw new AuthApiError(await readError(resp), resp.status);
  }
}

/** Validate a stored token + fetch the current user's claims. */
export async function fetchMe(token: string): Promise<AuthUser> {
  const base = baseUrl();
  if (base === null) {
    throw new AuthApiError("appApiBaseUrl is unset");
  }
  const resp = await fetch(`${base}/api/me`, {
    headers: { Accept: "application/json", "access-token": token },
    cache: "no-store",
  });
  if (!resp.ok) {
    throw new AuthApiError(await readError(resp), resp.status);
  }
  const data = (await resp.json()) as { user: AuthUser };
  return data.user;
}
